import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useUSSDState } from './ussdState';

interface USSDSimulatorProps {
  onClose?: () => void;
}

export const USSDSimulator: React.FC<USSDSimulatorProps> = ({ onClose }) => {
  const { session, isSessionExpired, endSession } = useUSSDState();
  const [timeRemaining, setTimeRemaining] = useState(60);

  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      if (isSessionExpired()) {
        endSession();
        return;
      }

      const remaining = Math.ceil((session.expiresAt - Date.now()) / 1000);
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [session, isSessionExpired, endSession]);

  if (!session) {
    return (
      <Card className="w-full max-w-sm mx-auto p-6 bg-background border-border">
        <div className="text-center space-y-4">
          <h2 className="text-lg font-semibold text-foreground">USSD Simulator</h2>
          <p className="text-sm text-muted-foreground">
            Dial *123# to start a session
          </p>
          {onClose && (
            <Button variant="outline" onClick={onClose} className="w-full">
              Close
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-sm mx-auto bg-background border-border shadow-lg">
      {/* Phone Header */}
      <div className="bg-card border-b border-border p-3 flex items-center justify-between">
        <span className="text-xs text-muted-foreground font-mono">USSD Session</span>
        <span className="text-xs text-muted-foreground font-mono">
          {timeRemaining}s
        </span>
      </div>

      {/* USSD Screen */}
      <div className="bg-accent/10 p-4">
        <div className="bg-background/90 rounded border border-border p-3 font-mono text-sm min-h-[180px]">
          <div className="space-y-1 text-foreground">
            {session.currentMenu && renderMenuContent(session)}
          </div>
        </div>
      </div>

      {/* Phone Keypad Hint */}
      <div className="bg-card border-t border-border p-3">
        <div className="text-xs text-center text-muted-foreground font-mono">
          Use number keys to navigate
        </div>
      </div>
    </Card>
  );
};

function renderMenuContent(session: any) {
  const menu = session.currentMenu;
  
  if (!menu) return null;

  const lines: string[] = [];
  
  // Title (truncated to 20 chars)
  if (menu.label) {
    lines.push(truncate(menu.label, 20));
    lines.push(''.padEnd(20, '-'));
  }

  // Menu items or input
  if (menu.type === 'menu' && menu.children) {
    menu.children.forEach((child: any, index: number) => {
      const option = `${index + 1}. ${child.label}`;
      lines.push(truncate(option, 20));
    });
  } else if (menu.type === 'input' && menu.inputConfig) {
    lines.push(truncate(menu.inputConfig.placeholder, 20));
    lines.push('_'.repeat(Math.min(session.inputValue.length + 1, 20)));
    lines.push('');
    lines.push('0. Submit  00. Back');
  } else if (menu.type === 'display') {
    // Display content
    const content = menu.label;
    const contentLines = content.split('\n');
    contentLines.forEach((line: string) => {
      lines.push(truncate(line, 20));
    });
  }

  // Ensure we have exactly 6 lines
  while (lines.length < 6) {
    lines.push('');
  }

  return lines.slice(0, 6).map((line, i) => (
    <div key={i} className="h-5 leading-5">
      {line}
    </div>
  ));
}

function truncate(str: string, length: number): string {
  if (str.length <= length) return str.padEnd(length, ' ');
  return str.substring(0, length - 1) + '…';
}
